import { useEffect, useRef, useState } from 'react'
import {
  api, ApiError,
  type CallQuestion, type IncidentAnswer, type IncidentDetail,
} from '../lib/api'
import { QuestionFields, isMultiple, outstanding, prune, visible, type Asking } from '../lib/questions'
import { AnswerSync, afterSuccess, type Notice } from '../lib/answerSync'
import { vehicleFromAnswer } from '../lib/vehicles'

/** The nature's questions, on a call that is already open.
 *
 *  THE OTHER HALF OF "QUESTIONS NEVER BLOCK A DISPATCH". The intake form
 *  refuses to hold a call up for an unanswered question and says so — "add them
 *  after the broadcast" — and this is where that promise is kept. Without it
 *  the intake would be telling a dispatcher to do something the product gives
 *  him no way to do, which is the shape CLAUDE.md keeps recording: a route that
 *  is complete, gated and reachable from nothing on screen.
 *
 *  IT IS UNGATED, and that is the route's decision rather than this file's.
 *  Opening a call needs no permission, so answering the questions that opening
 *  it asked cannot need one — and its cost would be a dispatcher refused
 *  mid-call with nothing to tell a denied permission from a broken screen.
 *
 *  AND IT WORKS ON A CLOSED CALL, unlike every lifecycle button beside it. An
 *  answer is a fact about the job rather than a step in it, and a call logged
 *  after an outage is typed up in full once it is over.
 */
export function CallAnswers({
  call, onChanged,
}: {
  call: IncidentDetail
  /** A save may have moved the priority and may have flagged the call, and both
   *  of those are drawn by the queue behind this panel. */
  onChanged: () => void
}) {
  const [tree, setTree] = useState<CallQuestion[]>(call.questions)
  /** THE CALLER NOT STAYING (077) asks what is asked only then. */
  const callAsking = { callerLeaving: call.caller_on_scene === false }
  const [answers, setAnswers] = useState<Record<string, string>>(
    () => seed(call.answers, call.questions, callAsking))
  const [saving, setSaving] = useState(false)
  /** The one sentence on the panel, with what it is about. See `Notice`. */
  const [notice, setNotice] = useState<Notice | null>(null)
  const error = notice?.text ?? null
  /** The same, readable from inside a save finishing after later renders. */
  const standing = useRef<Notice | null>(null)
  function say(next: Notice | null) {
    standing.current = next
    setNotice(next)
  }
  /** What the LAST SAVE did to the urgency: the server's own two branches, and
   *  neither of them is silent. Cleared by the next save. */
  const [moved, setMoved] = useState<'raised' | 'held' | null>(null)

  /** The form and the saves, kept in step. What the server holds, what the
   *  form holds, and WHEN each answer was last changed by hand -- which is what
   *  decides, after a reply or a failure, what is shown and what the next
   *  save sends. The rules, and what each simpler rule lost, are in
   *  `lib/answerSync.ts`; `web/scripts/answer-sync-scenarios.mjs` replays
   *  those losses against it.
   *
   *  A MISSING KEY IS NOT AN EMPTY ONE — keys that are not sent are left
   *  exactly as they stand and a key sent as "" clears that answer — so what
   *  is sent is only what changed. Sending the whole form instead would
   *  rewrite `answered_at` and the attribution on every answer somebody else
   *  gave earlier in the call. */
  const sync = useRef<AnswerSync>()
  if (!sync.current) sync.current = new AnswerSync(answers)

  /** The saves, in the order they were made. See `save`. */
  const chain = useRef<Promise<void>>(Promise.resolve())

  /** The tree the form was last drawn against, readable from inside a save
   *  that finishes long after the render that started it. */
  const asks = useRef<CallQuestion[]>(call.questions)

  /** A NATURE PUT ON OR TAKEN OFF (076), here or by a colleague: the panel
   *  STAYS -- its save chain, its record of what was typed when, a box still
   *  being typed in -- and only what is asked changes. Remounting it threw
   *  all of that away, and a pick of several answers was then toggled from a
   *  stale list and could erase one. */
  const natureKey = (call.natures ?? []).map((n) => n.position).join(',')
  const drawnFor = useRef(natureKey)
  useEffect(() => {
    if (drawnFor.current === natureKey) return
    drawnFor.current = natureKey
    const before = new Set(asks.current.map((q) => q.key))
    const now = new Set(call.questions.map((q) => q.key))
    const fromCall = seed(call.answers, call.questions, callAsking)
    const fresh = Object.fromEntries(
      Object.entries(fromCall).filter(([key]) => !before.has(key)))
    sync.current!.reshape([...before].filter((key) => !now.has(key)), fresh)
    asks.current = call.questions
    setTree(call.questions)
    show({ ...sync.current!.form }, call.questions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natureKey])

  /** A change made on the form, by hand. Every one comes through here. */
  function edit(next: Record<string, string>) {
    sync.current!.edit(next)
    setAnswers(next)
  }

  /** Draw what a save's outcome left, pruned against the tree it came with. */
  function show(merged: Record<string, string>, questions: CallQuestion[]) {
    const pruned = prune(questions, merged, callAsking)
    sync.current!.show(pruned)
    setAnswers(pruned)
  }

  /** Answers whose question this nature no longer asks — retired, or taken off
   *  the list since. THEY ARE FACTS A DISPATCHER RECORDED and they carry their
   *  own frozen prompt, so they are shown and never edited: the server refuses
   *  a key its tree does not have, and deleting them would let tidying the
   *  catalogue rewrite finished jobs. */
  const asked = new Set(tree.map((q) => q.key))
  /** AND ANSWERS THIS CALL DOES NOT ASK NOW, though the nature still does: a
   *  question since moved under "the caller is not staying" on a call where
   *  he stays (077), or under an answer this call never gave. They stay on
   *  the record and go to whoever accepts, so they are SHOWN -- never only
   *  sent. */
  const askedNow = new Set(visible(
    tree, given(call.answers, tree), callAsking).map((q) => q.key))
  const historic = call.answers.filter(
    (a) => !asked.has(a.question_key) || !askedNow.has(a.question_key))

  const stillToAsk = outstanding(tree, answers, callAsking)

  /** Store one settled answer.
   *
   *  SERIALISED THROUGH A CHAIN, NOT BY DISABLING THE CONTROLS. Two writes at
   *  once would be two requests deciding the same tree from two different
   *  starting points, and the second would answer with a shape that does not
   *  include the first — the failure the three inline equipment writes had. But
   *  the obvious fix, disabling the fields while a save is in flight, drops the
   *  very press that starts the race: clicking a chip while a typed answer is
   *  still in its box blurs the box, which saves, which disables the chip
   *  underneath the click. So the presses queue instead, and nothing is
   *  refused for being quick.
   *
   *  THE FORM GOES BACK IF THE SERVER REFUSES. A stale screen is answered 409 —
   *  the question was reworded, or an option withdrawn, since this call was
   *  opened — and leaving the refused answer on screen would show a fact that
   *  is not on the record. What goes back is only what this save was made
   *  from.
   */
  function save(next: Record<string, string>) {
    // WHEN IT WAS MADE, counted here and not when it is sent.
    const madeAt = sync.current!.made()
    // Each save waits for the one before it. `store` puts its own refusals on
    // screen and does not rethrow, so this catch is a backstop: a link that
    // rejected for any other reason would otherwise leave a rejected promise
    // in `chain`, and every later answer would be dropped onto it silently.
    chain.current = chain.current.then(() => store(next, madeAt)).catch(() => {})
  }

  async function store(next: Record<string, string>, madeAt: number) {
    // Composed NOW, not when the save was made: a save queued behind a slow one
    // must not re-send what the one before it just stored -- or was refused.
    const body = sync.current!.body(next)
    if (Object.keys(body).length === 0) return
    setSaving(true)
    try {
      const outcome = await api.incidentAnswers(call.id, body)
      // THE SERVER'S ANSWER IS THE FORM, except where the dispatcher has moved
      // on since this save was made. One answer can reveal three questions and
      // hide four, so this redraws from what came back rather than from what
      // was sent — the browser decides what is drawn and the server decides
      // what is true.
      const settled = seed(outcome.answers, outcome.questions, callAsking)
      setTree(outcome.questions)
      asks.current = outcome.questions
      const asking = new Set(outcome.questions.map((q) => q.key))
      show(sync.current!.stored(settled, asking, madeAt), outcome.questions)
      setMoved(outcome.priority_raised ? 'raised'
        : outcome.priority_held ? 'held' : null)
      // WHAT WAS STORED DIFFERENTLY FROM WHAT WAS SENT, SAID WHY. A vehicle
      // whose year was not a year is stored without one, and the server says
      // so in `notes`; the other drops are a screen that went stale.
      const notes = outcome.notes ?? {}
      const stale = outcome.dropped.filter((key) => !Object.hasOwn(notes, key))
      const said = [...Object.values(notes)]
      if (stale.length) {
        said.push(`${stale.length} answer(s) were not stored because the`
          + ' questions changed while this call was open. Answer them again.')
      }
      // WHAT IS ALREADY ON SCREEN GOES ONLY WHEN WHAT IT WAS ABOUT IS SETTLED
      // -- see `Notice` for the two messages a simpler rule wiped. A stale drop
      // cannot be answered again on this tree, so it is a `failure` that the
      // next save made after it clears; a note about a stored answer is not.
      const kept = afterSuccess(standing.current, body, madeAt)
      say(said.length
        ? {
          text: said.join(' '),
          keys: Object.keys(notes).length ? Object.keys(notes) : stale,
          at: sync.current!.made(),
          kind: Object.keys(notes).length ? 'note' : 'failure',
        }
        : kept)
      onChanged()
    } catch (e) {
      // JUDGED, OR NOT. A 4xx is the server saying no to this save, and it
      // must not be sent again with the next one. Anything else -- the network,
      // a server restarting -- judged nothing, and the next save carries it.
      const refused = e instanceof ApiError
        && e.status >= 400 && e.status < 500
        && e.status !== 401 && e.status !== 408 && e.status !== 429
      const asking = new Set(asks.current.map((q) => q.key))
      show(sync.current!.failed(asking, madeAt, refused), asks.current)
      say({
        text: e instanceof ApiError ? e.message : 'That answer was not saved.',
        keys: Object.keys(body),
        at: sync.current!.made(),
        kind: 'failure',
      })
    } finally {
      setSaving(false)
    }
  }

  if (tree.length === 0 && historic.length === 0) return null

  return (
    <div className="box qbox" style={{ marginTop: 15 }}>
      <h4>
        Questions
        {tree.length > 0 && (
          <span className={`pill ${stillToAsk.length ? '' : 'go'}`}>
            {stillToAsk.length === 0
              ? 'all answered'
              : `${stillToAsk.length} unanswered`}
          </span>
        )}
        {/* FOUR WORDS BESIDE THE HEADING, not a paragraph underneath and not a
            `title` nobody hovers. There is no Save button on this panel and
            that has to be legible, which is the lesson the member record's
            phone rows paid for. */}
        <span className="lbl">{saving ? 'saving…' : 'saved as you answer'}</span>
      </h4>
      <div className="bd">
        {error && <div className="notice" role="alert">{error}</div>}

        {/* THE URGENCY MOVED, OR WAS DELIBERATELY LEFT. The server writes the
            same two outcomes into the thread; this is so the dispatcher sees it
            at the moment he answers rather than by reading back down the
            record. */}
        {moved === 'raised' && (
          <p className="qalert" role="status">
            Priority raised — this call is now at the top of the queue. Change
            it on the queue if that is wrong.
          </p>
        )}
        {moved === 'held' && (
          <p className="qalert" role="status">
            Flagged. The urgency was left where it was set, and the answer is on
            the thread.
          </p>
        )}

        {tree.length === 0 ? (
          <p className="footnote" style={{ margin: 0 }}>
            This call’s nature of call no longer asks anything. What was answered
            at the time is below.
          </p>
        ) : (
          <div className="qgrid">
          <QuestionFields
            tree={tree}
            answers={answers}
            // THE WORDING EACH ANSWER WAS GIVEN UNDER. `call.answers` carries
            // the frozen prompt; the form draws the live one. Handing both
            // over is what lets it say so when they differ -- see the prop's
            // own comment for the failure that hides otherwise.
            askedAs={Object.fromEntries(
              call.answers.map((a) => [a.question_key, a.prompt]))}
            alertNote={alertNote}
            onChange={edit}
            onCommit={save}
            // Each nature's questions under its name, with two or more (076).
            sections={(call.natures ?? []).map((n) => n.nature_label)}
            asking={callAsking}
            // The working call is where a skipped question that expects an
            // answer shows as not confirmed. The intake form does not pass it:
            // a question not reached yet is not a question skipped.
            unconfirmed
          />
          </div>
        )}

        {historic.length > 0 && (
          <div className="qhistoric">
            {historic.map((a) => (
              <div className={`qold ${a.is_alert ? 'flagged' : ''}`} key={a.id}>
                <span className="qoldq">{a.prompt}</span>
                <span className="qolda">{a.answer}</span>
                {/* The note that went with it, frozen with the answer. */}
                {a.responder_note && <span className="qoldnote">{a.responder_note}</span>}
                {/* The prompt AS ASKED, which is not necessarily the wording on
                    the settings screen today — that is what freezing is for. */}
                <span className="lbl">
                  {asked.has(a.question_key) ? 'not asked on this call now' : 'no longer asked'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** What a flagged answer means on a call that is already open.
 *
 *  IT DOES NOT NAME AN URGENCY. Whether the raise happened depends on whether a
 *  dispatcher had already chosen one when the call was opened, and that is not
 *  knowable from here — the transient line above says which branch the LAST
 *  save took, and the queue card says what the priority is now. A sentence
 *  guessing it would be wrong half the time, which is worse than not saying it.
 */
function alertNote(question: CallQuestion): string {
  return question.raises_priority
    ? 'Flagged — shown in red on the ticket and on the queue, and it takes the'
      + ' call to the top unless an urgency was chosen when it was opened.'
    : 'Flagged — shown in red on the ticket and on the queue.'
}

/** The stored answers as a form, and ONLY the ones this tree still asks.
 *
 *  `prune` drops the rest, which is what stops the next save from sending a key
 *  the server's tree does not have — that is a 409 on the whole save, over an
 *  answer nobody touched. Those are drawn separately and never edited.
 *
 *  A VEHICLE IS SEEDED FROM ITS PARTS, not from `answer`. The stored sentence
 *  is not what the form sends, so seeding from it would make every save of any
 *  OTHER answer look like a change to the vehicle — and the server would be
 *  handed a sentence where it reads the form's JSON, and refuse it.
 */
function seed(
  stored: IncidentAnswer[], tree: CallQuestion[], asking: Asking,
): Record<string, string> {
  return prune(tree, given(stored, tree), asking)
}

/** The stored answers as the form holds them, before anything is pruned.
 *  Also the correction form's starting point (079). */
export function given(
  stored: IncidentAnswer[], tree: CallQuestion[],
): Record<string, string> {
  const vehicle = new Set(
    tree.filter((q) => q.kind === 'vehicle').map((q) => q.key))
  const several = new Set(tree.filter(isMultiple).map((q) => q.key))
  const map: Record<string, string> = {}
  for (const answer of stored) {
    // SEVERAL ANSWERS (076) FROM THEIR LIST, not from the sentence, for the
    // vehicle's reason: "Front left, Rear right" is not what the form sends.
    // A question unticked since takes its one answer, as the server reads it
    // (`call_questions.as_given`).
    const values = answer.answer_values ?? []
    map[answer.question_key] = vehicle.has(answer.question_key)
      ? vehicleFromAnswer(answer)
      : values.length && several.has(answer.question_key) ? JSON.stringify(values)
      : values.length === 1 ? values[0]
      : answer.answer
  }
  return map
}
